import { useGetConversationsQuery } from '@/api/messagesApi';
import { HeaderGeneral } from '@/components/layout';
import { ServiceBackground } from '@/components/DybrComponents/ServiceBackground';
import Checkbox from '@/components/FormElements/Checkbox';
import Loading from '@/components/Shared/Loading';
import ProfileLink from '@/scenes/Management/Community/ProfileLink';
import { useActiveProfile, useTokenState } from '@/store/localStorage/useTokenState';
import { selectActiveProfileAugmented } from '@/store/user/selectors/selectActiveProfileAugmented';
import MessageEnvelope from '@/styles/Icons/MessageEnvelope';
import { classNames } from '@/utils/helpers/classNames';
import { formatDateTime } from '@/utils/helpers/formatTimeDate';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import css from './InboxPage.module.css';
import { Footer } from '@/components/layout';

export function ConversationList() {
  const [checkedAll, setCheckedAll] = useState<boolean>(false);

  const [token] = useTokenState();
  const isLoggedIn = Boolean(token);
  const { activeProfileId } = useActiveProfile();
  const ownProfile = useSelector(state => selectActiveProfileAugmented(state, { activeProfileId }));

  const {
    data: conversationsData,
    isLoading: loading,
    error: queryError
  } = useGetConversationsQuery({ profileId: ownProfile?.id || '' }, { skip: !isLoggedIn || !ownProfile?.id });

  const conversations = isLoggedIn ? conversationsData?.data || [] : [];
  const total = isLoggedIn ? conversationsData?.meta?.totalDialogs || 0 : 0;
  const totalUnread = isLoggedIn ? conversationsData?.meta?.unreadDialogs || 0 : 0;
  const error = queryError
    ? 'Не удалось получить список разговоров'
    : !isLoggedIn
    ? 'Войдите, чтобы увидеть список разговоров'
    : '';

  function getStatusMessage(): string {
    if (conversations.length > 0) {
      if (totalUnread === 0) {
        return `Всего разговоров ${total}, непрочитанных сообщений нет`;
      } else {
        return `Всего разговоров ${total}, из них с новыми сообщениями ${totalUnread}`;
      }
    }
    if (conversations.length === 0) {
      return `У вас ещё нет сообщений. Напишите своё первое!`;
    }
    return '';
  }

  return (
    <>
      <ServiceBackground data-testid="feed-scenes">
        <HeaderGeneral title="разговоры" />
        <div className={css.container}>
          {loading && <Loading />}
          {error && <div className={css.error}>{error}</div>}
          {!error && !loading && conversations.length === 0 && (
            <>
              <div className={css.status}>{getStatusMessage()}</div>
            </>
          )}
          {!error && !loading && conversations.length > 0 && (
            <>
              <div className={css.status}>{getStatusMessage()}</div>
              <table className={css.table}>
                <thead>
                  <tr>
                    <th className={css.columnCheckbox} style={{ display: 'none' }}>
                      {/* @ts-expect-error - Checkbox component has loose prop requirements */}
                      <Checkbox
                        inputClassName={css.checkbox}
                        checked={checkedAll}
                        onChange={() => {
                          setCheckedAll(!checkedAll);
                        }}
                        invertedColor={true}
                      />
                    </th>
                    <th className={css.columnProfile}>ник пользователя</th>
                    <th className={css.columnMessages}>сообщения</th>
                    <th className={css.columnDate}>дата</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map(conversation => {
                    return (
                      <tr
                        key={conversation.profile.id}
                        className={classNames({
                          [css.unreadMessagesRow]: conversation.unreadMessageCount > 0
                        })}
                      >
                        <td className={css.columnCheckbox} style={{ display: 'none' }}>
                          {/* @ts-expect-error - Checkbox component has loose prop requirements */}
                          <Checkbox
                            inputClassName={css.checkbox}
                            checked={conversation.checked}
                            onChange={() => {
                              conversation.checked = !conversation.checked;
                            }}
                          />
                        </td>
                        <td className={css.columnProfile}>
                          <ProfileLink
                            profile={conversation.profile}
                            className={css.profileName}
                            link={`/direct/${conversation.profile.id}`}
                          >
                            {conversation.unreadMessageCount !== 0 && (
                              <div className={`${css.unreadMessagesCollapsed}`}>
                                <Link className={`${css.unreadMessagesMark}`} to={`/direct/${conversation.profile.id}`}>
                                  {conversation.unreadMessageCount}
                                </Link>
                              </div>
                            )}
                          </ProfileLink>
                        </td>
                        <td className={css.columnMessages}>
                          {conversation.unreadMessageCount > 0 && (
                            <>
                              <div className={css.unreadMessagesMark}>{conversation.unreadMessageCount}</div>
                              <span>{' / '}</span>
                            </>
                          )}
                          {conversation.messageCount}
                        </td>
                        <td className={css.columnDate}>{formatDateTime(conversation.lastMessagePublishedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
          {isLoggedIn && !loading && (
            <div className={css.help}>
              Чтобы написать сообщение, нажмите на аватар пользователя, а затем нажмите на{' '}
              <span title="личные сообщения">
                <MessageEnvelope className={css.helpIcon} />{' '}
              </span>
              в меню
            </div>
          )}
        </div>
      </ServiceBackground>

      <Footer withLemur />
    </>
  );
}
